/**
 * GeoManager — Map Operations
 *
 * Leaflet map initialization, markers, zones, and view controls.
 */

import { state, config } from './state.js';
import { api } from './api.js';
import { escapeHtml, showNotification } from './utils.js';

export function initMap(onMapClick) {
    let containerId = 'geo-map';
    let container = document.getElementById(containerId);

    if (!container) {
        containerId = 'geo-map-embed';
        container = document.getElementById(containerId);
    }

    if (!container || state.map) return;

    state.map = L.map(containerId, {
        center: [46.603354, 1.888334],
        zoom: 6,
        zoomControl: false,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap contributors</a>'
    }).addTo(state.map);

    if (typeof L.markerClusterGroup !== 'undefined') {
        state.markerClusterGroup = L.markerClusterGroup({
            showCoverageOnHover: true,
            maxClusterRadius: 60,
            spiderfyOnMaxZoom: true,
            disableClusteringAtZoom: 16,
            chunkedLoading: true,
            iconCreateFunction: function(cluster) {
                const count = cluster.getChildCount();
                let size = 'small';
                if (count >= 10) size = 'medium';
                if (count >= 50) size = 'large';

                return L.divIcon({
                    html: `<div class="geo-cluster geo-cluster-${size}"><span>${count}</span></div>`,
                    className: 'geo-cluster-icon',
                    iconSize: L.point(40, 40),
                });
            },
        });
        if (state.clusteringEnabled) {
            state.map.addLayer(state.markerClusterGroup);
        }
    }

    if (state.mapClickHandler) {
        state.map.off('click', state.mapClickHandler);
    }

    state.mapClickHandler = function(e) {
        if (onMapClick) onMapClick(e.latlng.lat, e.latlng.lng);
    };
    state.map.on('click', state.mapClickHandler);
}

export async function updateMapMarkers(selectPointFn) {
    if (!state.map) return;

    Object.values(state.markers).forEach(marker => {
        if (state.markerClusterGroup) {
            state.markerClusterGroup.removeLayer(marker);
        } else {
            state.map.removeLayer(marker);
        }
    });
    state.markers = {};

    Object.values(state.zones).forEach(zone => {
        state.map.removeLayer(zone);
    });
    state.zones = {};

    if (state.convexHullLayer) {
        state.map.removeLayer(state.convexHullLayer);
        state.convexHullLayer = null;
    }

    const hiddenFolderIds = new Set(
        state.folders
            .filter(f => f.is_visible === false || f.is_visible === 0)
            .map(f => f.id)
    );

    const visiblePoints = state.points.filter(point => {
        if (!point.folder_id) return true;
        return !hiddenFolderIds.has(point.folder_id);
    });

    visiblePoints.forEach(point => {
        addMarkerToMap(point, selectPointFn);
    });

    const zonePromises = visiblePoints
        .filter(point => point.zone_type !== 'none' && point.zone_value)
        .map(point => addZoneToMap(point));

    await Promise.all(zonePromises);

    if (visiblePoints.length > 0) {
        fitMapToPoints();
    }
}

export function addMarkerToMap(point, selectPointFn) {
    let markerIcon;

    if (point.icon_type === 'custom' && point.icon_value) {
        markerIcon = L.divIcon({
            className: 'geo-marker geo-marker-custom',
            html: `
                <div class="geo-marker-inner geo-marker-custom-inner">
                    <img src="${point.icon_value}" alt="icon" style="width:28px;height:28px;object-fit:contain;">
                </div>
            `,
            iconSize: [32, 32],
            iconAnchor: [16, 32],
            popupAnchor: [0, -32],
        });
    } else {
        const iconData = config.icons.preset[point.icon_value] || config.icons.preset['default'];
        markerIcon = L.divIcon({
            className: 'geo-marker',
            html: `
                <div class="geo-marker-inner" style="background-color: ${point.color || config.defaultColor}">
                    <i class="fas ${iconData.icon}"></i>
                </div>
            `,
            iconSize: [32, 32],
            iconAnchor: [16, 32],
            popupAnchor: [0, -32],
        });
    }

    const marker = L.marker([point.lat, point.lng], { icon: markerIcon });

    marker.bindPopup(`
        <div class="geo-popup">
            <strong>${escapeHtml(point.name)}</strong>
            ${point.description ? `<p>${escapeHtml(point.description)}</p>` : ''}
            <div class="geo-popup-actions">
                <button onclick="GeoManager.selectPoint('${point.id}')">Détails</button>
                <button onclick="GeoManager.showEditPointModal('${point.id}')">Modifier</button>
            </div>
        </div>
    `);

    marker.on('click', () => {
        if (selectPointFn) selectPointFn(point.id);
    });

    if (state.markerClusterGroup) {
        state.markerClusterGroup.addLayer(marker);
    } else {
        marker.addTo(state.map);
    }

    state.markers[point.id] = marker;
}

export async function addZoneToMap(point) {
    const zoneColor = point.zone_color || config.defaultZoneColor;
    const zoneOpacity = point.zone_opacity || config.defaultZoneOpacity;

    if (point.zone_type === 'km') {
        const circle = L.circle([point.lat, point.lng], {
            radius: point.zone_value * 1000,
            color: zoneColor,
            fillColor: zoneColor,
            fillOpacity: zoneOpacity,
            weight: 2,
        });
        circle.addTo(state.map);
        state.zones[point.id] = circle;

    } else if (point.zone_type === 'minutes') {
        try {
            const response = await api.fetch('/isochrone', {
                method: 'POST',
                body: JSON.stringify({
                    lat: point.lat,
                    lng: point.lng,
                    minutes: point.zone_value,
                    transport: point.zone_transport || 'driving-car',
                }),
            });

            if (response.success) {
                if (response.type === 'polygon' && response.geojson) {
                    const layer = L.geoJSON(response.geojson, {
                        style: {
                            color: zoneColor,
                            fillColor: zoneColor,
                            fillOpacity: zoneOpacity,
                            weight: 2,
                        },
                    });
                    layer.addTo(state.map);
                    state.zones[point.id] = layer;
                } else {
                    const circle = L.circle([point.lat, point.lng], {
                        radius: response.radius_km * 1000,
                        color: zoneColor,
                        fillColor: zoneColor,
                        fillOpacity: zoneOpacity,
                        weight: 2,
                        dashArray: '5, 5',
                    });
                    circle.addTo(state.map);
                    state.zones[point.id] = circle;
                }
            }
        } catch (error) {
            console.warn(`Erreur calcul isochrone pour ${point.id}:`, error);
            const radiusKm = (point.zone_value / 60) * 50;
            const circle = L.circle([point.lat, point.lng], {
                radius: radiusKm * 1000,
                color: zoneColor,
                fillColor: zoneColor,
                fillOpacity: zoneOpacity,
                weight: 2,
                dashArray: '5, 5',
            });
            circle.addTo(state.map);
            state.zones[point.id] = circle;
        }
    }
}

export function fitMapToPoints() {
    if (state.points.length === 0 || !state.map) return;
    const bounds = L.latLngBounds(state.points.map(p => [p.lat, p.lng]));
    state.map.fitBounds(bounds, { padding: [50, 50] });
}

export function centerOnPoint(pointId) {
    const point = state.points.find(p => p.id === pointId);
    if (point && state.map) {
        state.map.setView([point.lat, point.lng], 15);
        highlightMarker(pointId);
    }
}

export function highlightMarker(pointId) {
    Object.keys(state.markers).forEach(id => {
        const el = state.markers[id].getElement();
        if (el) el.classList.remove('highlighted');
    });

    const marker = state.markers[pointId];
    if (marker) {
        const el = marker.getElement();
        if (el) el.classList.add('highlighted');
        marker.openPopup();
    }
}

export function filterPointsByFolder(folderId) {
    Object.keys(state.markers).forEach(pointId => {
        const point = state.points.find(p => p.id === pointId);
        const marker = state.markers[pointId];

        if (folderId && point && point.folder_id !== folderId) {
            marker.setOpacity(0.3);
        } else {
            marker.setOpacity(1);
        }
    });
}
